/**
 * Static content for the Cours d'été funnel. Mirrors the brevet constants
 * pattern but holds summer-specific copy. Subjects and the personalised program
 * are now API-driven (curriculum-backed) — only the offer copy, niveaux, slots
 * and pricing live here as static content.
 */

import { API_URL } from "@data/constants/apiConstants"

// Clé de variante d'engagement (toggle). Défaut = SANS engagement (toggle OFF).
export const SUMMER_VARIANT_NO_COMMITMENT = "no_commitment"
export const SUMMER_VARIANT_COMMITMENT = "commitment_12m"

// Grille tarifaire Cours d'été — DEUX variantes d'engagement, indexées par le
// NOMBRE DE CRÉNEAUX hebdo (chaque créneau = 1 h/sem). Miroir de
// `config/summer.php → funnel.pricing`. Source de vérité côté front ; la config
// API (show() → pricing_grid) prime quand elle est chargée (cf. resolveSummerPricing).
//   SANS engagement (défaut) : 1 → 69 (~~139~~, -50 %), 2 → 109 (~~199~~, -45 %), 3 → 139 (~~289~~, -52 %)
//   AVEC engagement 12 mois  : 1 → 49 (~~89~~, -45 %),  2 → 69 (~~119~~, -42 %),  3 → 89 (~~169~~, -47 %)
// Paliers 4–10 : scellés (grille finale), jusqu'à 10 créneaux/sem (5 jours × 2
// horaires EN VENTE). Le €/h (prix ÷ heures/mois) décroît du palier 1 à ~4 puis plafonne
// vers 10 €/h de 5 à 10 (tarif volume). Prix barrés ALIGNÉS sur la même logique de
// remise que les paliers 1–3 (≈ -50 % SANS engagement → strike ≈ 2× le prix ;
// ≈ -45 % AVEC engagement → strike ≈ 1,8× le prix), chaque prix et strike finit par 9.
export const SUMMER_PRICING_GRID = {
    [SUMMER_VARIANT_NO_COMMITMENT]: {
        1: { price: 69, strike: 139 },
        2: { price: 109, strike: 199 },
        3: { price: 139, strike: 289 },
        4: { price: 179, strike: 359 },
        5: { price: 219, strike: 439 },
        6: { price: 259, strike: 519 },
        7: { price: 299, strike: 599 },
        8: { price: 349, strike: 699 },
        9: { price: 389, strike: 779 },
        10: { price: 429, strike: 859 },
    },
    [SUMMER_VARIANT_COMMITMENT]: {
        1: { price: 49, strike: 89 },
        2: { price: 69, strike: 119 },
        3: { price: 89, strike: 169 },
        4: { price: 119, strike: 219 },
        5: { price: 149, strike: 269 },
        6: { price: 179, strike: 329 },
        7: { price: 209, strike: 379 },
        8: { price: 239, strike: 429 },
        9: { price: 269, strike: 489 },
        10: { price: 289, strike: 529 },
    },
}

// Cadence mensuelle Cours d'été par nombre de créneaux hebdo (chaque créneau =
// 1 h/sem ; ~4,33 sem/mois, pas ×4). Miroir de `config/summer.php →
// funnel.hours_per_month` ; la grille API (staticData → `hours_per_month`) prime
// quand elle est chargée. Repli hors-ligne pour le cockpit (heures affichées ==
// heures facturées par la vente différée).
export const SUMMER_HOURS_PER_MONTH = {
    1: 4,
    2: 9,
    3: 13,
    4: 17,
    5: 22,
    6: 26,
    7: 30,
    8: 35,
    9: 39,
    10: 43,
}

// Borne un nombre de créneaux aux bornes de la grille (1..10) pour qu'une valeur
// hors-grille retombe sur le palier le plus proche.
const clampSlotCount = (slotCount, variantGrid) => {
    const counts = Object.keys(variantGrid).map(Number)
    const min = Math.min(...counts)
    const max = Math.max(...counts)
    const n = slotCount || min
    return Math.max(min, Math.min(max, n))
}

const variantKey = (withCommitment) =>
    withCommitment ? SUMMER_VARIANT_COMMITMENT : SUMMER_VARIANT_NO_COMMITMENT

/**
 * Résout le pricing pour un NOMBRE DE CRÉNEAUX hebdo + une variante d'engagement
 * (défaut SANS engagement). Lit en priorité la grille exposée par l'API
 * (`config.pricing_grid` côté show(), forme à deux variantes), avec la grille
 * constante en fallback. Retourne `{ price, strike, discountPercent }` où
 * `strike` vaut null quand il n'y a pas de promo.
 *
 * @param {number} weeklyHours     Nombre de créneaux hebdo (chaque créneau = 1 h).
 *                                 Utiliser `weeklyHoursFromSlots(slotIds)`.
 * @param {boolean} [withCommitment]  Variante engagement 12 mois (défaut false).
 * @param {object} [apiGrid]        Grille API optionnelle ({ "no_commitment":
 *                                  { "1": {price, strike, discount_percent}, ... },
 *                                  "commitment_12m": { ... } }).
 */
export const resolveSummerPricing = (
    weeklyHours,
    withCommitment = false,
    apiGrid = null
) => {
    const key = variantKey(withCommitment)
    const constantVariant = SUMMER_PRICING_GRID[key]
    const count = clampSlotCount(weeklyHours, constantVariant)

    // 1) Grille API (source de vérité quand chargée).
    const apiVariant = apiGrid?.[key]
    const apiEntry = apiVariant?.[String(count)] ?? apiVariant?.[count]
    if (apiEntry && apiEntry.price != null) {
        return {
            price: apiEntry.price,
            strike: apiEntry.strike ?? null,
            discountPercent: apiEntry.discount_percent ?? 0,
        }
    }

    // 2) Grille constante (fallback). Badge arrondi à la dizaine INFÉRIEURE
    // (même règle que le backend) : chiffre rond, jamais supérieur à la remise
    // réelle (51,9 % → 50, 45,2 % → 40).
    const entry = constantVariant[count]
    const price = entry.price
    const strike = entry.strike && entry.strike > price ? entry.strike : null
    const discountPercent = strike
        ? Math.floor(((strike - price) / strike) * 10) * 10
        : 0
    return { price, strike, discountPercent }
}

/**
 * Prix plancher « à partir de » affiché quand AUCUN créneau n'est coché (état
 * vierge du configurateur). Dérivé de la grille chargée — API en priorité,
 * grille constante en fallback — jamais d'une constante dédiée : le plancher
 * suit toujours la grille réellement servie.
 *
 * @param {boolean} [withCommitment]  Variante engagement 12 mois (défaut false).
 * @param {object}  [apiGrid]         Grille API optionnelle (même forme que
 *                                    pour resolveSummerPricing).
 */
export const resolveSummerFloorPrice = (
    withCommitment = false,
    apiGrid = null
) => {
    const key = variantKey(withCommitment)
    const apiPrices = Object.values(apiGrid?.[key] || {})
        .map((entry) => entry?.price)
        .filter((price) => Number.isFinite(price))
    if (apiPrices.length) return Math.min(...apiPrices)
    return Math.min(
        ...Object.values(SUMMER_PRICING_GRID[key]).map((entry) => entry.price)
    )
}

// Matières prioritaires — on en prend AU PLUS autant que d'heures de cours, et
// volontiers MOINS (ex. 3h tout en maths = 1 matière sur 3 créneaux). Les
// créneaux sont le maître : ajouter une matière au-delà du nb de créneaux en
// ajoute un (le prix suit). Le plafond dur est donc le nombre total de créneaux
// hebdo offerts (5 jours × 2 horaires = 10), aligné sur le plafond
// `hours_per_week` backend. La liste elle-même vient de l'API (matières à
// chapitres validés pour la classe). Front-enforced, capped backend-side.
export const SUMMER_MAX_SUBJECTS = 10

// Objectif d'été (ids alignés sur UpdateSummerAnswersRequest).
export const SUMMER_OBJECTIVES = [
    {
        id: "combler_lacunes",
        emoji: "🧩",
        label: "Combler ses lacunes",
        subLabel: "Reprendre les bases fragiles",
    },
    {
        id: "garder_rythme",
        emoji: "🎯",
        label: "Garder le rythme",
        subLabel: "Entretenir les acquis sans décrocher",
    },
    {
        id: "prendre_avance",
        emoji: "🚀",
        label: "Prendre de l'avance",
        subLabel: "Aborder l'année suivante en confiance",
    },
]

// Niveau ressenti (visual cards).
export const SUMMER_NIVEAUX = [
    { id: "en_difficulte", emoji: "😟", label: "En difficulté" },
    { id: "moyen", emoji: "😐", label: "Moyen" },
    { id: "correct", emoji: "🙂", label: "Correct" },
    { id: "bon_eleve", emoji: "😎", label: "Bon élève" },
]

// Créneaux Cours d'été — édition 5×3 : 5 jours (lun→ven) × 3 horaires d'1 h
// chacun, deux le MATIN (10h30-11h30 et 11h30-12h30) et un le SOIR
// (18h30-19h30). 15 créneaux catalogués, tous d'1 h (pas de variante de durée).
// Les horaires sont déclarés une fois et croisés avec les jours : les ids générés
// (`mon_1030_1130`, `mon_1130_1230`, …) matchent `SummerSlotMapper::SLOT_DEFINITIONS`
// backend. `SUMMER_SLOT_DAYS` porte la structure (jour → ses horaires) que le
// configurateur affiche en 5 lignes ; `SUMMER_SLOTS` reste la liste plate
// (résolution id → label/horaire dans les récaps).
const SUMMER_SLOT_TIMES = [
    { key: "1030_1130", time: "10h30-11h30", period: "morning" },
    { key: "1130_1230", time: "11h30-12h30", period: "morning" },
    { key: "1830_1930", time: "18h30-19h30", period: "evening" },
]

const SUMMER_SLOT_WEEKDAYS = [
    { day: "mon", label: "Lundi" },
    { day: "tue", label: "Mardi" },
    { day: "wed", label: "Mercredi" },
    { day: "thu", label: "Jeudi" },
    { day: "fri", label: "Vendredi" },
]

export const SUMMER_SLOT_DAYS = SUMMER_SLOT_WEEKDAYS.map(({ day, label }) => ({
    day,
    label,
    options: SUMMER_SLOT_TIMES.map(({ key, time, period }) => ({
        id: `${day}_${key}`,
        label: `${label} ${time}`,
        time,
        period,
    })),
}))

// Liste plate de TOUS les créneaux CATALOGUÉS — utilisée pour résoudre un id →
// label/horaire dans les récaps, y compris pour un créneau saturé qu'une famille
// détient déjà. Chaque créneau dure 1 h.
export const SUMMER_SLOTS = SUMMER_SLOT_DAYS.flatMap((d) =>
    d.options.map((o) => ({
        id: o.id,
        day: d.day,
        label: d.label,
        period: o.period,
        time: o.time,
    }))
)

// Créneaux SATURÉS — repli hors-ligne du `full_slot_ids` servi par l'API (config
// backend `summer.full_timeslots`). Catalogués donc résolvables en label, mais
// PLUS proposés à l'inscription : le formulaire les masque. Le matin 10h30 est
// saturé depuis l'édition 2026, d'où l'ouverture du 11h30. Garder synchro avec la
// config backend, qui fait foi dès que staticData / la config funnel est chargée.
export const SUMMER_FULL_SLOT_IDS = [
    "mon_1030_1130",
    "tue_1030_1130",
    "wed_1030_1130",
    "thu_1030_1130",
    "fri_1030_1130",
]

// Ids des créneaux saturés, backend prioritaire (config funnel `full_slot_ids`),
// repli sur la constante ci-dessus hors-ligne. Un tableau vide servi par l'API est
// respecté (= plus aucun créneau saturé), d'où le test sur `Array.isArray`.
export const resolveFullSlotIds = (funnelConfig = null) =>
    Array.isArray(funnelConfig?.full_slot_ids)
        ? funnelConfig.full_slot_ids
        : SUMMER_FULL_SLOT_IDS

// Créneaux de repli pour les écrans AVAL (reveal programme / checkout) atteints
// — cas limite — sans créneaux en state (le program endpoint exige des
// slot_ids, sinon 422). Ce n'est PLUS une pré-sélection du configurateur : à
// l'arrivée, rien n'est coché et le prix affiche le plancher « à partir de »
// dérivé de la grille (cf. resolveSummerFloorPrice).
// Le matin 11h30, PAS le 10h30 : un repli sur un créneau saturé ferait 422 le
// programme (le back valide contre les créneaux en vente).
export const SUMMER_DEFAULT_SLOT_IDS = [
    "mon_1130_1230",
    "tue_1130_1230",
    "wed_1130_1230",
]

// Set de tous les ids catalogués (pour filtrer une sélection périmée).
const SLOT_IDS = new Set(SUMMER_SLOTS.map((s) => s.id))

// Pack d'heures hebdo = nombre de créneaux choisis (chaque créneau = 1 h). C'est
// la clé du palier tarifaire ET `hours_per_week` côté backend.
export const weeklyHoursFromSlots = (slotIds) =>
    (slotIds || []).filter((id) => SLOT_IDS.has(id)).length

// Ne garde que les ids de créneaux reconnus par le schéma courant. Sert à
// normaliser une sélection reprise (checkout_params) : un id périmé/étranger
// ferait sous-compter `weeklyHoursFromSlots` et déclencherait un retrait auto de
// matières au 1er toggle (cf. SummerConfiguratorScreen.toggleSlot).
export const filterValidSlotIds = (slotIds) =>
    (slotIds || []).filter((id) => SLOT_IDS.has(id))

// Ne garde que les créneaux EN VENTE : catalogués ET non saturés. C'est ce qu'on
// applique à une sélection REPRISE (state d'avant la fermeture d'un créneau), pour
// que le parent re-choisisse au lieu de faire 422 sur un créneau plus en vente.
// Le back applique la même normalisation en servant la reprise — ceci couvre le
// state local (redux-persist / Context) qui ne repasse pas par l'API.
export const filterOfferedSlotIds = (slotIds, funnelConfig = null) => {
    const full = new Set(resolveFullSlotIds(funnelConfig))
    return filterValidSlotIds(slotIds).filter((id) => !full.has(id))
}

// Dérivation classe (année en cours) → classe à la rentrée. Pré-remplissage
// corrigeable. Miroir de SummerFunnelController::deriveNextClass. Source unique
// partagée par l'écran classe ET la couture de normalisation à la reprise.
export const NEXT_CLASS = {
    CE2: "CM1", CM1: "CM2", CM2: "6ème", "6ème": "5ème", "5ème": "4ème",
    "4ème": "3ème", "3ème": "2nde", "2nde": "1ère", "1ère": "Tle", Tle: "Tle", Autre: "Autre",
}

// Normalise une classe saisie/persistée sans accent (ex. "3eme" → "3ème", "tle"
// → "Tle") vers le libellé canonique attendu par NEXT_CLASS et les chips.
export const normaliseClass = (raw) => {
    if (!raw) return raw
    const map = { "3eme": "3ème", "4eme": "4ème", "5eme": "5ème", "6eme": "6ème", "1ere": "1ère", tle: "Tle" }
    return map[String(raw).toLowerCase()] || raw
}

// Classe de rentrée dérivée d'une classe (déjà normalisée). null si inconnue.
export const deriveClassRentree = (klass) => NEXT_CLASS[klass] || null

// Mois de l'édition (numéro de mois = ce qu'attend le backend). Par défaut les
// deux ; le parent peut prendre juillet seul ou août seul.
export const SUMMER_MONTHS = [
    { id: 7, label: "Juillet" },
    { id: 8, label: "Août" },
]

// Icône matière depuis le resource API (`icon_path` host-relative, ex.
// `/images/subjects/maths.png`) → URL absolue. null si pas d'icône.
// L'API renvoie désormais une URL absolue (asset() → CDN d'assets en prod) ;
// on garde le préfixe API_URL en rétro-compat si un chemin relatif arrive.
export const subjectIconUri = (iconPath) =>
    iconPath
        ? iconPath.startsWith("http")
            ? iconPath
            : `${API_URL}${iconPath}`
        : null

// Capitalise la 1ʳᵉ lettre d'une chaîne ("mer. 1 juil." → "Mer. 1 juil.").
// Partagé par les écrans/composants du funnel pour formater les dates fr.
export const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : str

// Garanties Cours d'été (même forme que NewSubscriptionGuaranteesCard :
// {icon,label,subtitle}). Affichées dans le récap du checkout via la carte
// partagée NewSubscriptionGuaranteesCard.
export const SUMMER_GUARANTEES = [
    {
        icon: "shield-alt",
        label: "Sans engagement",
        subtitle: "Résiliable à tout moment, sans frais ni conditions",
    },
    {
        icon: "sliders-h",
        label: "Changez de formule à tout moment",
        subtitle:
            "Heures par semaine, matières et créneaux ajustables depuis votre espace parent",
    },
    {
        icon: "star",
        label: "Tuteurs notés 4,9/5",
        subtitle: "Plus de 25 000 heures de cours dispensées",
    },
    {
        icon: "user-check",
        label: "Changez de tuteur quand vous voulez",
        subtitle: "Celui qui convient le mieux à votre enfant",
    },
]
